import type { PropsWithChildren } from "react";
import { PreferredContactMethod, PreferredContactMethodDisplayNames } from "@remi/enums/preferred-contact-method";
import { formatPhoneNumberForDisplay, PhoneNumberFormat } from "@remi/formatting/phone";
import { RnCopy, RnEnvelope, RnMessage, RnPhone, RnStarFilled } from "@remi/icons/next";
import { RemiBadge } from "@remi/next-ui";
import { can } from "@remi/permissions";

import { useUserProfile } from "~/contexts/UserProfileContext";
import { cn } from "~/lib/utils";
import { GENERIC_PHONE_LABEL } from "../../contact-phone-label";
import { appendProxyOrgLabel } from "../../proxy-phone-grouping";
import { CallButton } from "./CallButton";
import { CopyButton } from "./CopyButton";
import { EmailButton } from "./EmailButton";
import { TextButton } from "./TextButton";

type ContactRowProps = PropsWithChildren<{
  label: string;
  value: string;
  proxyValue?: string;
  className?: string;
  icon?: React.ReactNode;
}>;

// Out of the flow because reserving width at all times is what squeezed the value into truncating.
// Absolute alone paints above the value, so no z-index; the backdrop matches every surface this renders
// on. Needs a `relative` parent and a `group` ancestor, which is why callers cannot just drop in buttons.
const ContactRowActions = ({ children }: PropsWithChildren) => (
  <span className="absolute inset-y-0 right-0 flex items-center gap-0.5 bg-background-secondary transition-opacity md:opacity-0 md:group-hover:opacity-100">
    {children}
  </span>
);

export const EmailRowActions = ({ email }: { email: string }) => (
  <ContactRowActions>
    <EmailButton muted icon={<RnEnvelope />} email={email} />
    <CopyButton muted icon={<RnCopy />} value={email} valueType="email" />
  </ContactRowActions>
);

export const PhoneRowActions = ({ personId, number }: { personId: string; number: string }) => {
  const { user } = useUserProfile();

  return (
    <ContactRowActions>
      {can.person.viewDetails(user) && <TextButton muted icon={<RnMessage />} personId={personId} number={number} />}
      <CallButton muted icon={<RnPhone />} number={number} />
      <CopyButton muted icon={<RnCopy />} value={number} valueType="phone" />
    </ContactRowActions>
  );
};

export const ContactRow = ({ label, value, proxyValue, icon, children, className }: ContactRowProps) => {
  return (
    <div className={cn("group col-span-full grid grid-cols-subgrid", className)}>
      <div className="@xs:col-span-1 col-span-full flex items-center gap-0.5">{label}</div>
      <div className="relative @xs:col-span-1 col-span-full flex items-center gap-1 overflow-hidden">
        <span title={value} className="type-xs min-w-0 select-text truncate text-text-primary">
          {value}
        </span>
        {icon}
        {children}
      </div>
      {proxyValue && (
        <div className="col-span-full flex @xs:grid @xs:grid-cols-subgrid gap-2">
          <span className="type-xs text-text-tertiary">Proxy:</span>
          <span title={proxyValue} className="type-xs flex-1 select-text truncate text-text-tertiary">
            {proxyValue}
          </span>
        </div>
      )}
    </div>
  );
};

type PreferredContactBadgeProps = {
  method: PreferredContactMethod;
};

// shrink-0, so the badge is either fully legible or absent, never a partial pill. A shrinking badge
// collapses to a 16px sliver showing only the star, which human testing read as a rendering failure
// rather than a deliberate state. The value truncates instead; it has a title tooltip and a copy button.
export const PreferredContactBadge = ({ method }: PreferredContactBadgeProps) => (
  <RemiBadge size="sm" variant="yellow" icon={<RnStarFilled aria-hidden="true" />} className="shrink-0">
    {PreferredContactMethodDisplayNames[method]}
    <span className="sr-only">&nbsp;is the preferred contact method</span>
  </RemiBadge>
);

type EmailContactRowProps = {
  email: string;
  isPrimary: boolean;
  preferredContactMethod?: PreferredContactMethod;
  className?: string;
  showButtons?: boolean;
};

export const EmailContactRow = ({
  email,
  isPrimary,
  preferredContactMethod,
  className,
  showButtons = true,
}: EmailContactRowProps) => {
  const isPreferred = preferredContactMethod === PreferredContactMethod.EMAIL;

  return (
    <ContactRow
      className={className}
      key={email}
      label={"Email"}
      value={email}
      icon={isPrimary && isPreferred ? <PreferredContactBadge method={preferredContactMethod} /> : undefined}
    >
      {showButtons && <EmailRowActions email={email} />}
    </ContactRow>
  );
};

type PhoneContactRowProps = {
  phone?: string;
  proxyNumber?: string;
  proxyOrgName?: string;
  personId: string;
  isPrimary: boolean;
  label?: string;
  preferredContactMethod?: PreferredContactMethod;
  className?: string;
  showButtons?: boolean;
};

export const PhoneContactRow = ({
  phone,
  proxyNumber,
  proxyOrgName,
  personId,
  isPrimary,
  label = GENERIC_PHONE_LABEL,
  preferredContactMethod,
  className,
  showButtons = true,
}: PhoneContactRowProps) => {
  const displayNumber = phone ?? proxyNumber;
  const displayProxyNumber = phone ? proxyNumber : null;
  const isProxyOnlyRow = !phone && !!proxyNumber;

  if (!displayNumber) {
    return null;
  }

  const formattedDisplayNumber = formatPhoneNumberForDisplay(displayNumber, { format: PhoneNumberFormat.NATIONAL });
  const isPreferred =
    preferredContactMethod === PreferredContactMethod.TEXT || preferredContactMethod === PreferredContactMethod.CALL;

  return (
    <ContactRow
      className={className}
      key={displayNumber}
      label={label}
      value={isProxyOnlyRow ? appendProxyOrgLabel(formattedDisplayNumber, proxyOrgName) : formattedDisplayNumber}
      proxyValue={
        displayProxyNumber
          ? appendProxyOrgLabel(
              formatPhoneNumberForDisplay(displayProxyNumber, { format: PhoneNumberFormat.NATIONAL }),
              proxyOrgName,
            )
          : undefined
      }
      icon={isPrimary && isPreferred ? <PreferredContactBadge method={preferredContactMethod} /> : undefined}
    >
      {showButtons && <PhoneRowActions personId={personId} number={displayNumber} />}
    </ContactRow>
  );
};
